"use client";

import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { classNames } from "@saas/shared/utils";

import { BaseInput, BaseInputProps } from "../base-input/base-input";

import { useElementSize } from "usehooks-ts";

export interface PhoneInputProps
  extends Omit<BaseInputProps, "fixedLabel" | "showCounter" | "onChange"> {
  /**
   * Phone number prefix
   *
   * @default +62
   */
  prefixInput?: string;
  onChange?: (value: string) => void;
  value?: string;
}

/**
 * Phone number input uses to save user's phone number.
 * Only support number format.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ prefixInput = "+62", onChange, value, className, ...props }, ref) => {
    const [prefixRef, { width }] = useElementSize();
    const PADDING = 20;
    const [internalValue, setInternalValue] = useState(
      value?.replace(/^\+?62/, "") || ""
    );

    const inputRef = useRef<HTMLInputElement>(null);

    // Expose inputRef to parent ref
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    useEffect(() => {
      setInternalValue(value?.replace(/^\+?(620|62)/, "") || "");
    }, [value]);

    const formatPhoneNumber = (input: string) => {
      let formatted = input.replace(/[^0-9]/g, "");
      if (formatted.startsWith("62")) {
        formatted = formatted.slice(2);
      } else if (formatted.startsWith("0")) {
        formatted = formatted.slice(1);
      }
      return formatted;
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setInternalValue(e.target.value);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      const formatted = formatPhoneNumber(e.target.value.trim());
      setInternalValue(formatted);
      onChange?.(formatted ? prefixInput + formatted : "");
    };

    return (
      <div className={"relative"}>
        <BaseInput
          {...props}
          type={"tel"}
          fixedLabel={true}
          className={classNames(
            "placeholder-transparent focus:placeholder-transparent",
            className
          )}
          placeholder={"8xxxxxxxxxx"}
          style={{ paddingLeft: width + PADDING }}
          onChange={handleChange}
          onBlur={handleBlur}
          value={internalValue}
          ref={inputRef}
        />

        <span
          ref={prefixRef}
          className={classNames(
            "body-b1 absolute top-[17px] left-[17px] block",
            props.error ? "text-red-R700" : "text-he"
          )}
        >
          {prefixInput}
        </span>
      </div>
    );
  }
);

PhoneInput.displayName = "PhoneInput";

export default PhoneInput;
